/**
 * Shared type definitions for the SOMA Outcome Planner web app.
 *
 * Extracted from main.ts as part of OMON-031 Phase 2. Pure type-only
 * module — no runtime code, safe to import with `import type`.
 */
import type { PromotionProvenance } from "./catalogue/provenance-view";

export type { PromotionProvenance };

/**
 * Which side of an escalation threshold is the concerning one. `"above"` is
 * the historical behaviour and the default for every plan predating the field.
 */
export type EscalationDirection = "above" | "below";

export interface ObservationDefinition {
  name: string;
  title: string;
  status: string;
  /** FHIR active flag on the catalogue fact; retired facts set this false. */
  active?: boolean;
  description: string;
  category: Array<{ coding: Array<{ system?: string; code: string; display: string }> }>;
  qualifiedValue: Array<{
    rangeCategory: string;
    range: { low: { value: number }; high: { value: number } };
    context: { text: string; description?: string };
  }>;
  _smartGoalTemplate?: string;
  /**
   * OMON-174: canonical librarian-authored prompting text for the Monitored
   * capability — what/how a carer should handle this monitored event. The
   * monitored-event sibling of `_smartGoalTemplate`: snapshot-copied onto the
   * plan's `_monitoredEvents[]` entry as `_prompt` at add-time (freeze-at-add,
   * the ADR-0007 snapshot carve-out — Ed 2026-06-10). No boilerplate default.
   */
  _monitoredEventPrompt?: string;
  /**
   * Zammad #49088 (Ed, 2026-09-24): carer guidance for a monitored event — the
   * event sibling of a developmental goal's "Carer Guidance Prompts", but ONE
   * text per event (not per level). Markdown, `## HEADING` sections, no
   * `# title`. Catalogue-only and late-binding: read live from the OD, never
   * snapshot onto a plan (unlike `_monitoredEventPrompt`).
   */
  _monitoredEventGuidance?: string;
  /**
   * OMON-232 (Ed, 2026-10-01): the fact's parts — the general "parts"
   * pattern (`clients/event-parts.ts`). Each item is a FHIR component
   * definition plus our `_prompt` extension (the part's default prompt).
   */
  component?: unknown[];
  /**
   * OMON-117: STC catalogue metadata distinguishing developmental
   * (`"soma"`) from monitored-event (`"three-state-ordinal"`) ODs. Reads
   * may see undefined on legacy docs pre-OMON-117 backfill; default to
   * `"soma"` if absent.
   */
  _measurementType?: string;

  // --- Trackable v3 (ADR-0019) — see backlog/docs/TRACKABLES_DATA_SHAPE.md ---
  /**
   * Two-tier link: a generic *parent* fact references its variant-children
   * (the FHIR-native `ObservationDefinition.hasMember`). Each reference is a
   * `_observationDefinition/<id>` path. Present on parent facts only.
   */
  hasMember?: Array<{ reference: string }>;
  /**
   * Inverse of `hasMember` — a *variant* points back to its parent. STC
   * extension (FHIR models only the forward direction). Present on variants.
   */
  _variantOf?: { reference: string };
  /** Marks a generic parent fact (top of a two-tier group). */
  _trackableParent?: boolean;
  /**
   * Disambiguates the recording attribute where `permittedDataType` is too
   * coarse: `attainment-5 | rating | tally | boolean`. Evolves
   * `_measurementType` (fork C). Default `attainment-5` when absent on a
   * developmental fact.
   */
  _recordingKind?: string;
  /**
   * OMON-173: librarian-controlled within-bucket display order for the catalogue
   * tree (drag-to-reorder). Scoped relative to siblings in the same bucket
   * (goal-category + optional goal-subcategory). STC ext; mirrors the existing
   * `GoalInfo._order` (OMON-028) convention one layer down. Absent ⇒ sorts last,
   * then by title (until the backfill seeds it).
   */
  _order?: number;
  /**
   * OMON-205: seed this fact onto every NEW plan's draft. The planner can
   * still remove it for a specific client.
   *
   * ☠️ A property of the DEFINITION, deliberately — auto-inclusion used to be
   * a hardcoded list of fact names in the app, which went stale the moment
   * the definition was re-created under a new id and silently stopped seeding
   * anything. A flag on the doc travels with the doc.
   */
  _autoInclude?: boolean;
  // --- OMON-172: librarian per-fact policy flags (definition-layer defaults) ---
  // The librarian grants the affordance on the client-agnostic definition; the
  // planner activates/overrides it per client (definition-vs-instance split,
  // mirrors the D/M/S capability pattern). STC extensions — see TRACKABLES_DATA_SHAPE.md §7.
  /** Planner may set a reduction target per client against this fact. */
  _targetable?: boolean;
  /** Planner may escalate; surfaces the carer "notify office" affordance (OMON-128). */
  _escalatable?: boolean;
  /** Librarian default escalation threshold (planner can override per client). */
  _escalationThresholdDefault?: number;
  /** Default: carer can't close a day's entries without completing this (OMON-127).
   *  Legacy since ADR-0035 am.8: read as "every role" when `_shiftRolesDefault`
   *  is absent (see shift-roles.ts `defaultsOf`). */
  _mandatory?: boolean;
  /** ADR-0035 am.8: the carer roles that owe this item by default (seeded onto
   *  every plan item). Default none. */
  _shiftRolesDefault?: string[];
  /** ADR-0035 am.8: the librarian has fixed the role set; a plan can't change
   *  it, and it wins over what an existing plan item says. The Shift log. */
  _rolesLocked?: boolean;
  /** FHIR `permittedDataType` — `["integer"]`, `["boolean"]`, etc. */
  permittedDataType?: string[];
  /**
   * ADR-0048 — where this item came from, when it did not come from here.
   * Written by the `catalogue_promote` Cloud Function onto the production
   * copy, and read back by catalogue/provenance-view.ts. Absent on
   * everything production authored itself, which is most of the catalogue.
   */
  _provenance?: PromotionProvenance;
}

/**
 * The capability "affordance menu" a fact supports, read from its `category`
 * codings (system `…/trackable-capability`). A fact may support more than one;
 * the per-client plan activates a subset (ADR-0019). Matched BY SYSTEM, never
 * by slot index (monitored-event docs already fill `category[0]`).
 */
export type TrackableCapability = "developmental" | "monitored" | "safety";

export const TRACKABLE_CAPABILITY_SYSTEM =
  "https://sweettreeconnect.com/fhir/CodeSystem/trackable-capability";

/** Read the capability menu off an ObservationDefinition's category codings. */
export function trackableCapabilities(od: ObservationDefinition): TrackableCapability[] {
  const out: TrackableCapability[] = [];
  for (const entry of od.category || []) {
    for (const c of entry?.coding || []) {
      if (c.system === TRACKABLE_CAPABILITY_SYSTEM) {
        out.push(c.code as TrackableCapability);
      }
    }
  }
  return out;
}

/** The variant-child doc IDs of a parent fact (parsed from `hasMember`). */
export function variantChildIds(od: ObservationDefinition): string[] {
  return (od.hasMember || [])
    .map((m) => m.reference?.split("/").pop() || "")
    .filter(Boolean);
}

export interface ActivityDefinition {
  name: string;
  title: string;
  status: string;
  usage: string;
  documentation: Array<{ type: string; label: string; display: string }>;
  /** Ed 2026-09-29: carer guidance as ONE free-form text (guidance/single-text.ts).
   *  Absent ⇒ composed from `documentation[]` on read. */
  _carerGuidanceText?: string;
  category: Array<{ coding: Array<{ system?: string; code: string; display: string }> }>;
}

export interface GoalCategoryNode {
  code: string;
  display: string;
  order: number;
  status: string;
}

export interface GoalSubcategoryNode {
  code: string;
  goalCategoryCode: string;
  display: string;
  order: number;
  status: string;
}

export interface PatientInfo {
  docId: string;
  name: string;
  status: string;
  hasPlan: boolean;
  /** OMON-035: latest active CarePlan version for list-row pill rendering. */
  planVersion?: number;
  /** OMON-035: true when this EoC has a planner's draft in progress. */
  hasDraft?: boolean;
  /** The episode's area team (`managingOrganization`), for the Service
   *  filter (Tali, 2026-10-14). */
  teamOrgId?: string | null;
  /** The client's service line, walked from the team (loaders/services.ts).
   *  Filled in after the list loads; absent until then. */
  service?: string | null;
}

export interface CarePlanInfo {
  docId: string;
  title: string;
  status: string;
  created: string;
  goalRefs: string[];
  periodStart?: string;
  periodEnd?: string;
  /** OMON-035: version number on the supersede chain (v1, v2, ...). Pre-OMON-035
   *  documents lack this field; readers default to 1 when absent. */
  _version?: number;
  /** OMON-035: serialised builder state on draft documents (status:draft).
   *  Holds builderGoals + planMonths + planStartDate as JSON. Drafts
   *  don't materialise _goal docs until promotion to active. */
  _draftState?: string;
  /** OMON-035: ISO timestamp of the last successful autosave write. */
  _draftLastSavedAt?: string;
  /**
   * FHIR R5 `CarePlan.custodian` — who the plan is attributed to, and so who
   * a carer's "Notify office" reaches. Flattened to the `_person` doc id for
   * the UI; absent on plans saved before web 1.5.1.
   */
  custodianPersonId?: string;
  /**
   * True when a human chose that custodian rather than inheriting it from
   * the last save. ☠️ The builder must seed its picker from this, or opening
   * and saving a plan would silently unpin someone else's deliberate choice.
   */
  custodianPinned?: boolean;
}

export interface GoalInfo {
  docId: string;
  description: string;
  lifecycleStatus: string;
  achievementStatus: string;
  /** Stable hierarchy code, e.g. "life_skills". Absent on pre-OMON-028 goals. */
  categoryCode?: string;
  /** Display string for the category (fallback for pre-OMON-028 goals). */
  category?: string;
  /** The category code exactly as stored, resolvable or not. Carried so a
   *  republish keeps it instead of writing the "(unknown…)" label as the
   *  code (see catalogue/category-code.ts). */
  storedCategoryCode?: string;
  /** Stable aim code, e.g. "personal_finance". Absent on pre-OMON-028 goals. */
  subcategoryCode?: string;
  subcategory?: string;
  targetScore?: number;
  currentScore?: number;
  measureCode?: string;
  _baselineMonths?: number;
  _baselineFixed?: boolean;
  _trajectory?: number[];
  _smartGoal?: string;
  /** OMON-183: mandatory-per-shift on the goal doc (absent on older goals). */
  mandatory?: boolean;
  /** ADR-0035 am.8: roles that owe this goal (absent on goals written before). */
  shiftRoles?: import("./shift-roles").ShiftRoleCode[];
  /**
   * OMON-135: per-plan override of carer guidance. 5-string array (one
   * per SOMA level), snapshotted from `_activityDefinition.documentation[]`
   * at goal-add time and editable per-plan thereafter. Absent ⇒ readers
   * fall back to canonical catalogue guidance.
   */
  _carerGuidance?: string[];
  /** Ed 2026-09-29: per-plan carer guidance as ONE text; wins over everything. */
  _carerGuidanceText?: string;
  /** Librarian/planner-controlled within-category order. OMON-028. */
  _order?: number;
}

/**
 * A monitored event attached to a CarePlan (post-OMON-134).
 *
 * Distinct from `GoalInfo` because monitored events have different semantics:
 * - Recorded by their own regime (`_recordingKind`: boolean | rating | tally),
 *   not the SOMA progressive attainment scale
 * - Per-shift recording obligation, not progressive attainment
 * - Carried on `_carePlan._monitoredEvents[]` (not in `goal[]`)
 *
 * Read-side rendering for now (Phase 2 minimum). Adding new monitored events
 * to plans via planner UI is OMON-119 follow-up.
 */
export interface MonitoredEventInfo {
  /** The referenced _observationDefinition doc ID (e.g. "swearing", "seizures"). */
  odCode: string;
  /** Live-resolved title from catalogue (post-OMON-134 the OD is clean — no ** prefix). */
  title: string;
  /** Subcategory display string (live-resolved); undefined if not resolvable. */
  subcategory?: string;
  /** Per-instance target measure (e.g. 1, 3). Carried from the original _goal.target[0].detailInteger. */
  targetMeasure?: number;
  /** Review date for this monitored event on this plan. */
  dueDate?: string;
  /** Original _goal doc ID this entry was migrated from (audit). Present only for migrated entries. */
  migratedFromGoal?: string;
  /** Lifecycle hint from the original _goal — carried through migration. */
  lifecycleStatus?: string;
  /** Free-text notes (carried from original _goal.note if present). */
  notes?: unknown;
  /**
   * OMON-174: the prompting text for this monitored event on this plan —
   * the entry's `_prompt` snapshot when present, else live-resolved from the
   * OD's canonical `_monitoredEventPrompt` (legacy entries predate the field).
   */
  prompt?: string;
  /** OMON-232: the entry's per-part prompts (`_componentPrompts`), when the fact has parts. */
  componentPrompts?: Record<string, string>;
  /**
   * OMON-179/177: the entry's frozen per-shift-obligation values. Absent
   * field = the affordance wasn't granted when this entry was added;
   * explicit `false` = granted but deactivated by the planner.
   */
  mandatory?: boolean;
  /** OMON-358: roles that owe this entry (absent = derive from `mandatory`). */
  shiftRoles?: import("./shift-roles").ShiftRoleCode[];
  /** ADR-0035 am.9a: the entry's `_allowNotApplicable` — carers may answer
   *  "Not applicable on this shift" for it. Absent ⇒ false. */
  allowNotApplicable?: boolean;
  escalate?: boolean;
  escalationThreshold?: number;
  /** See EscalationDirection. Absent ⇒ "above". */
  escalationDirection?: EscalationDirection;
  targetValue?: number;
  /**
   * The fact's recording regime (`_recordingKind`), resolved live from the
   * catalogue at load time. Drives the planner's per-regime "review
   * visualisation pending" banner (ADR-0022) until each regime gets a proper
   * chart. Absent ⇒ treat as the default three-state ordinal.
   */
  recordingKind?: string;
}

/**
 * OMON-129 (single-plan transcript scope): one observation projected for the
 * Combined narrative tab — either stream, with or without a carer note.
 * Built by `loadPatientActuals` from the SAME `_observation` snapshot the
 * charts use (a second read-projection; nothing new stored).
 */
export interface NarrativeEntry {
  /** ISO effectiveDateTime. */
  date: string;
  /** Fact id (`code.coding[0].code` — the _observationDefinition doc id). */
  code: string;
  /** Which value shape the recording carried. "not-applicable" = ADR-0035
   *  am.9b: the carer's structured "Not applicable on this shift" answer (no
   *  value[x]; it answers the item but is never data — no chart plots it). */
  kind: "score" | "state" | "tally" | "not-applicable";
  /** 1–5 attainment score (kind === "score"). */
  score?: number;
  /** boolean recording code "yes" | "no" (kind === "state"). */
  state?: string;
  /** Raw numeric reading carried by `valueInteger` (rating regime, e.g. an O2
   * saturation of 97). Surfaced in the narrative chip for `rating` facts;
   * ignored for `tally` (whose valueInteger is always the occurrence sentinel 1). */
  value?: number;
  performerName?: string;
  /** am.9b: the reason's label (`dataAbsentReason.text`), kind "not-applicable" only. */
  notApplicableText?: string;
  /** am.9b: the reason code (day-shift | not-in-care | other), kind "not-applicable" only. */
  notApplicableReason?: string;
  /** The carer's free text — the narrative substance; absent = quiet marker. */
  note?: string;
}

/**
 * A single loaded observation enriched for the consolidated trajectory
 * chart. `monthIndex` is computed against plan start at load time.
 */
export interface ActualObservation {
  score: number;
  date: string;            // ISO date string
  performerName?: string;  // display name from performer[0].display
  performerId?: string;    // from performer[0].reference.id (canonical Pattern A)
  note?: string;           // from note[0].text
  monthIndex: number;      // 0-based index into the plan window
}

/**
 * A monitored event selected during plan creation, before saving.
 *
 * Parallel to `SelectedGoal` for the developmental side. Planner-authored
 * entries carry the minimum needed to render the in-flight list + write
 * the canonical `_carePlan._monitoredEvents[]` Reference on save. The
 * `dueDate` / `targetMeasure` / `_migratedFromGoal` fields that the
 * OMON-134 migration carried over from `_goal.target[]` are migration-
 * time only and not authored here.
 *
 * OMON-119 FULL (2026-05-22).
 */
export interface BuilderMonitoredEvent {
  /** _observationDefinition doc ID = canonical OD code (e.g. "swearing"). */
  odCode: string;
  /** OD title resolved from catalogue at pick time, for display in the builder. */
  title: string;
  /** Subcategory display (e.g. "Verbal Escalation") for the in-flight chip. */
  subcategory?: string;
  /**
   * OMON-174: planner-tunable prompting text. Seeded from the OD's canonical
   * `_monitoredEventPrompt` at pick time (or the existing entry's `_prompt`
   * when editing a plan); persisted onto the `_monitoredEvents[]` entry as
   * `_prompt` on save (snapshot, even if untouched — freeze-at-add-time).
   */
  prompt: string;
  /**
   * OMON-232 (Ed, 2026-10-01): per-part prompts for a fact with parts,
   * seeded from each part's default at pick time and saved onto the entry
   * as `_componentPrompts` (same freeze-at-add rule as `prompt`).
   */
  componentPrompts?: Record<string, string>;
  /**
   * OMON-179/177: per-client per-shift-obligation values, seeded from the
   * librarian's OMON-172 defaults at pick time, tuned per client, and
   * snapshotted onto the `_monitoredEvents[]` entry on save. Controls are
   * AFFORDANCE-GATED at render/save time (only granted flags render and
   * persist) — see `clients/monitored.ts`.
   */
  mandatory?: boolean;
  /** OMON-358: roles that owe this entry (absent = derive from `mandatory`). */
  shiftRoles?: import("./shift-roles").ShiftRoleCode[];
  /**
   * ADR-0035 am.9a: carers may answer "Not applicable on this shift" for this
   * item. Saved onto the `_monitoredEvents[]` entry as `_allowNotApplicable:
   * true` only when on (absent ⇒ off). Off by default, never seeded on.
   */
  allowNotApplicable?: boolean;
  escalate?: boolean;
  escalationThreshold?: number | null;
  /**
   * Which side of the threshold is the concerning one (Ed, 2026-09-02).
   * `"above"` (the default, and the historical behaviour) or `"below"` — some
   * facts are worrying when they DROP: fluid intake, meals eaten, hours slept.
   *
   * ☠️ Advice, not automation. Nothing in the estate compares a recording to
   * this threshold and acts — escalation reaches the office only when a carer
   * ticks "Notify office". This field changes what the carer is TOLD, and how
   * the planner's chart colours a breach. Audited 2026-09-02 across app.carer,
   * the Cloud Functions, the Temporal workers and the rules agents: no
   * automatic escalation machinery exists.
   *
   * Absent ⇒ `"above"`, so every existing plan keeps its current meaning.
   */
  escalationDirection?: EscalationDirection;
  targetOn?: boolean;
  targetValue?: number | null;
}

/**
 * OMON-149: a single carer derived from `_encounter.participant[]`.
 *
 * `practitionerDocId` is the `_practitioner` doc ID (the actor.reference.id).
 * `name` is resolved live from the `_practitioner` doc's `name[0]` shape;
 * falls back to the docId if no name is parseable.
 */
export interface CarerSummary {
  practitionerDocId: string;
  name: string;
}

/**
 * OMON-149: client-context block rendered above the SOMA plan view.
 *
 * Sources:
 *   - `age` + `birthDate` + `address*` from the `_person` doc reached via
 *     `EoC.subject[0].reference` (direct for test pilots, or via
 *     `_patient.link._person` for OneTouch-synced pilots).
 *   - `plannedCarers` + `todaysCarers` derived from `_encounter` docs
 *     whose `subject.reference.path == _patient/<id>` and
 *     `meta.source == "onetouch-sync"`. Empty arrays for test pilots
 *     without a `_patient` link (the encounter-side data simply doesn't
 *     exist for them).
 */
export interface ClientContextData {
  age?: number;
  birthDate?: string;
  addressSummary?: string;
  address?: ClientAddress;
  /** ADR-0046 §6: the client's phone, for their person card. */
  phone?: string;
  /**
   * OMON-149 follow-up: care manager's display name resolved from
   * `EoC.careManager[0].reference`. Populated for manually-seeded
   * pilots; absent for the ONE-004 auto-materialised cohort (OMON-158
   * territory). Undefined ⇒ "not assigned" empty-state in the UI.
   */
  careManager?: string;
  /**
   * OMON-149 follow-up: most common `area` value across the carers
   * assigned to this client (read from `_practitioner.extension` →
   * `onetouch-organisation` → `area`). Falls back to `workgroup` if
   * area absent. Undefined ⇒ no carers / no area data.
   */
  practice?: string;
  plannedCarers: CarerSummary[];
  todaysCarers: CarerSummary[];
  /**
   * ONE-015: the client's `_relatedPerson` mirrors (OneTouch-canonical
   * identity), projected for the client-information tile. Empty for
   * clients with no synced circle or no `_patient` link.
   */
  familyContacts?: import("./clients/family-contacts").FamilyContactSummary[];
  /** MONO-106: the `_patient` doc id — the avatar upload's owner target. */
  patientDocId?: string;
  /** MONO-106: storage path of the client avatar thumb, when one exists. */
  clientAvatarPath?: string;
}

/**
 * OMON-149: a single address line off a `_person.address[0]` entry.
 *
 * Defensive shape because Firestore data we're reading sometimes
 * serialises arrays as objects-keyed-by-string-index (the
 * "object-as-array" gotcha) — the loader normalises both shapes into
 * this interface.
 */
export interface ClientAddress {
  line?: string[];
  district?: string;
  city?: string;
  postalCode?: string;
}

/** An objective selected during plan creation, before saving */
export interface SelectedGoal {
  name: string;
  title: string;
  /** Display string for rendering; resolved live via resolveODHierarchy. */
  goalCategory: string;
  subcategory: string;
  /** Stable hierarchy code for persistence. OMON-028. */
  categoryCode: string;
  /** ADR-0035 am.8: the carer roles that owe this goal. */
  shiftRoles?: import("./shift-roles").ShiftRoleCode[];
  /** The goal doc's stored category code, even when unresolvable. */
  storedCategoryCode?: string;
  /** Stable aim code for persistence. OMON-028. */
  subcategoryCode: string;
  currentScore: number;
  targetScore: number;
  baselineMonths: number;
  smartGoal: string;
  /** OMON-183: mandatory-per-shift applies to goals too (Ed: "anything can
   *  be mandatory"). Seeded from the librarian's OD default; written to the
   *  _goal doc at publish. */
  mandatory: boolean;
  /**
   * OMON-135: per-plan carer-guidance override (5 strings, one per SOMA
   * level). Snapshotted at goal-add time from `_activityDefinition.documentation[]`
   * and edited per-plan via the goal-row UI. Empty strings allowed.
   */
  carerGuidance: string[];
  /**
   * Ed 2026-09-29: this plan's carer guidance as ONE text (`_goal._carerGuidanceText`),
   * or null/absent = the catalogue's. Seeded on load from `_carerGuidanceText`,
   * else from a meaningful legacy `_carerGuidance` (composed).
   */
  guidanceOverride?: string | null;
  trajectory: number[];
  _overrides: Set<number>;
  /** 0-based month offset from plan start when this goal begins (default 0) */
  goalStartMonth: number;
  /** 0-based month offset from plan start when this goal ends, exclusive (default planMonths) */
  goalEndMonth: number;
}
